'use client';

import { observer } from 'mobx-react-lite';
import Image from 'next/image';


import type { ReactNode } from 'react';


import { useSceneScreenshot } from '@/app/editor/hooks/useSceneScreenshot';
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { getPlayModeSceneDisplayName } from '@/editor/core/play-preview/playModeSceneDisplayName';
import { PlayLaunchMode } from '@/editor/core/play-preview/playPreviewTypes';
import { project, store } from '@/editor/store';
import { PlayModeSceneType } from '@/editor/store/editorStore';
import { cn } from '@/lib/utils';
const PLAY_MODE_THUMBNAIL_WIDTH = 320;
const PLAY_MODE_THUMBNAIL_HEIGHT = 180;
const PLAY_MODE_TOOLTIP_THUMBNAIL_WIDTH = 192;
const PLAY_MODE_TOOLTIP_THUMBNAIL_HEIGHT = 108;

/** Get label for play mode scene type */
export const getPlayModeSceneLabel = (type: PlayModeSceneType): string => {
  switch (type) {
    case PlayModeSceneType.GameDefault: return 'Game Default Scene';
    case PlayModeSceneType.CurrentScene: return 'Current Scene';
    default: return 'Game Default Scene';
  }
};

/** Parse scene filename and remove extension for UI labels */
export const getSceneNameWithoutExtension = getPlayModeSceneDisplayName;

/** Get scene path for a specific play mode type */
export const getScenePathForPlayModeType = (type: PlayModeSceneType): string | null => {
  switch (type) {
    case PlayModeSceneType.GameDefault:
      return store.defaultScene;
    case PlayModeSceneType.CurrentScene:
      return project.currentScene;
    default:
      return store.defaultScene;
  }
};

/** Get info component showing which scene will be loaded without extension */
export const getPlayModeSceneInfo = (type: PlayModeSceneType, scenePath: string | null): ReactNode => {
  const sceneNameWithoutExtension = getSceneNameWithoutExtension(scenePath);
  if (!sceneNameWithoutExtension) {
    return <span className="text-muted-foreground">Not set</span>;
  }
  return <span className="font-semibold">{sceneNameWithoutExtension}</span>;
};

/** Component to show scene screenshot thumbnail in fixed position */
export const SceneThumbnailPreview = observer(({ type }: { type: PlayModeSceneType }) => {
  const scenePath = getScenePathForPlayModeType(type);
  const screenshotUrl = useSceneScreenshot(scenePath).url;
  const sceneNameWithoutExtension = getSceneNameWithoutExtension(scenePath);

  if (!sceneNameWithoutExtension) {
    return (
      <div className="px-2 py-3 text-xs text-center text-muted-foreground">
        No scene selected
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-2 py-3">
      {screenshotUrl ? (
        <Image
          src={screenshotUrl}
          alt={sceneNameWithoutExtension}
          className="w-full h-auto rounded border border-border"
          width={PLAY_MODE_THUMBNAIL_WIDTH}
          height={PLAY_MODE_THUMBNAIL_HEIGHT}
          unoptimized
        />
      ) : (
        <div className="w-full aspect-video rounded border border-border bg-muted flex items-center justify-center">
          <span className="text-xs text-muted-foreground">No preview</span>
        </div>
      )}
      <div className="text-xs text-center font-medium truncate" title={sceneNameWithoutExtension}>
        {sceneNameWithoutExtension}
      </div>
    </div>
  );
});
SceneThumbnailPreview.displayName = 'SceneThumbnailPreview';

/** Component to show play mode tooltip with thumbnail */
export const PlayModeTooltipContent = observer(() => {
  const scenePath = store.playModeScenePath;
  const screenshotUrl = useSceneScreenshot(scenePath).url;
  const sceneNameWithoutExtension = getSceneNameWithoutExtension(scenePath);
  const currentMode = store.playModeSceneType;
  const currentLaunchMode = store.playLaunchMode;

  const launchModeLabel = (() => {
    switch (currentLaunchMode) {
      case PlayLaunchMode.NewWindow:
        return 'Play in New Window';
      case PlayLaunchMode.MobilePreview:
        return 'Play in Mobile Preview';
      case PlayLaunchMode.Embedded:
      default:
        return 'Play in Editor';
    }
  })();

  const playModeShortcutRowClass = (mode: PlayModeSceneType) =>
    cn(
      'flex items-center gap-2 text-xs',
      currentMode === mode
        ? 'text-active-foreground font-medium'
        : 'text-muted-foreground',
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm flex flex-col gap-1.5 p-2">
        <span>
          Start Play Mode
          <span className="text-foreground font-bold"> - {launchModeLabel}</span>
        </span>
        <KbdGroup className="flex flex-col items-start gap-1">
          <span
            className={playModeShortcutRowClass(PlayModeSceneType.GameDefault)}
            aria-current={currentMode === PlayModeSceneType.GameDefault ? 'true' : undefined}
          >
            <Kbd>F5</Kbd>
            <span>Game Default Scene</span>
          </span>
          <span
            className={playModeShortcutRowClass(PlayModeSceneType.CurrentScene)}
            aria-current={currentMode === PlayModeSceneType.CurrentScene ? 'true' : undefined}
          >
            <Kbd>F6</Kbd>
            <span>Current Scene</span>
          </span>
        </KbdGroup>
      </div>
      <div className="text-xs text-foreground font-medium">
        [{getPlayModeSceneLabel(store.playModeSceneType)}]
      </div>
      {screenshotUrl && (
        <Image
          src={screenshotUrl}
          alt={sceneNameWithoutExtension ?? 'Scene preview'}
          className="w-48 h-auto rounded border border-border"
          width={PLAY_MODE_TOOLTIP_THUMBNAIL_WIDTH}
          height={PLAY_MODE_TOOLTIP_THUMBNAIL_HEIGHT}
          unoptimized
        />
      )}
      {sceneNameWithoutExtension && (
        <div className="text-xs text-foreground font-medium text-center">{sceneNameWithoutExtension}</div>
      )}
    </div>
  );
});
PlayModeTooltipContent.displayName = 'PlayModeTooltipContent';
